import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Header.scss';
import logo from '../../../assets/images/vrp-logo.png';
import { company, navItems as NAV, primaryCta, providerPortal } from '../../../content/site.js';

/* Keep in sync with the burger breakpoint at the bottom of Header.scss. */
const MOBILE_NAV_QUERY = '(max-width: 920px)';

const LockIcon = () => (
  <svg className="vh-header__portal-icon" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 10V7a5 5 0 0 1 10 0v3M6 10h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z"
    />
  </svg>
);

/** Visible, focusable controls inside `root`, in DOM order. */
const focusablesIn = (root) =>
  root
    ? [...root.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.getClientRects().length > 0)
    : [];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const burgerRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Any navigation (including browser back/forward) closes the menu.
  useEffect(() => { setOpen(false); }, [pathname]);

  // While open: move focus into the menu, keep Tab inside header + menu, close on
  // Escape, and close if the viewport grows past the burger breakpoint (otherwise
  // the menu would stay up with its close button hidden and the page locked).
  useEffect(() => {
    if (!open) return undefined;

    menuRef.current?.focus();

    const mq = window.matchMedia(MOBILE_NAV_QUERY);
    const onResize = () => { if (!mq.matches) setOpen(false); };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = [...focusablesIn(headerRef.current), ...focusablesIn(menuRef.current)];
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      // The menu panel itself holds focus right after opening; let the browser move on naturally.
      if (active === menuRef.current) return;
      const inside = els.includes(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <header ref={headerRef} className={`vh-header ${scrolled ? 'is-scrolled' : ''} ${open ? 'is-open' : ''}`}>
        <div className="vh-header__bar u-container">
          <Link to="/" className="vh-header__brand" onClick={close} aria-label="Virginia Rehab Physicians home">
            <img className="vh-header__logo" src={logo} alt="Virginia Rehab Physicians" width="342" height="172" />
          </Link>

          <nav className="vh-header__nav" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink key={item.href} to={item.href} className="vh-header__link">
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="vh-header__actions">
            <a
              href={providerPortal.href}
              className="vh-header__portal"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LockIcon />
              {providerPortal.label}
            </a>
            <Link to={primaryCta.href} className="btn btn--gold vh-header__cta">
              {primaryCta.label}
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
            <button
              ref={burgerRef}
              type="button"
              className="vh-header__burger"
              aria-expanded={open}
              aria-controls="vh-mobile"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((o) => !o)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/*
        The mobile menu must live OUTSIDE <header>. The header's backdrop-filter makes it
        the containing block for position:fixed descendants, so a menu nested inside it is
        sized to the 88px header instead of the viewport and its links spill over the page.
      */}
      <div id="vh-mobile" ref={menuRef} className="vh-mobile" hidden={!open} tabIndex={-1}>
        <nav className="vh-mobile__nav" aria-label="Mobile">
          {NAV.map((item) => (
            <NavLink key={item.href} to={item.href} className="vh-mobile__link" onClick={close}>
              {item.label}
            </NavLink>
          ))}
          <Link to={primaryCta.href} className="btn btn--gold vh-mobile__cta" onClick={close}>
            {primaryCta.label}
            <span className="btn__arrow" aria-hidden="true">→</span>
          </Link>
          <a
            href={providerPortal.href}
            className="vh-mobile__portal"
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            <LockIcon />
            {providerPortal.label}
          </a>
        </nav>

        <address className="vh-mobile__contact">
          <a href={company.phoneHref}>{company.phone}</a>
          <a href={`mailto:${company.email}`}>{company.email}</a>
        </address>
      </div>
    </>
  );
};

export default Header;
